import {
  insertNodes,
  PlateRenderElementProps,
  setNodes,
  TImageElement,
  useEditorRef,
  Value,
} from '@udecode/plate';
import * as React from 'react';
import { Path, Transforms } from 'slate';
import { ReactEditor, useSelected } from 'slate-react';

import { ImageRender } from './img-renderer';
import { UploadImageEditor } from './upload-image';

const { useCallback } = React;

export type ImageElementProps = PlateRenderElementProps<Value> &
  TImageElement & {
    element: { width: number; caption?: { isOpen: boolean; text: any[] }; url?: string };
  };

export const ImageElement = (props: ImageElementProps) => {
  const { attributes, children, element, nodeProps } = props;
  const editor = useEditorRef();
  const isSelected = useSelected();
  const { url, width, caption = { isOpen: false, text: [{ children: [{ text: '' }] }] } } = element;

  const handleChangeImageWidth = useCallback(
    (width: number) => {
      const path = ReactEditor.findPath(editor, element);
      setNodes(editor, { width: width }, { at: path });
    },
    [editor]
  );

  const onToggleCaption = (value?: boolean) => () => {
    const path = ReactEditor.findPath(editor, element);
    setNodes(editor, { caption: { ...caption, isOpen: value ?? !caption.isOpen } }, { at: path });
  };

  const handleChangeCaption = useCallback(
    (text: string) => {
      const path = ReactEditor.findPath(editor as ReactEditor, element);
      setNodes(editor, { caption: { ...caption, text: [{ children: [{ text }] }] } }, { at: path });
    },
    [editor, element, caption]
  );

  const handleOnDelete = () => {
    const path = ReactEditor.findPath(editor, element);
    Transforms.removeNodes(editor, { at: path });
  };

  const handleOnDuplicate = () => {
    const path = ReactEditor.findPath(editor, element);
    const at = Path.next(path);
    const newElement = { ...element };
    if (newElement.id) {
      delete newElement.id;
    }
    insertNodes(editor, newElement, {
      at,
    });
  };

  const onSetUrl = useCallback(
    (url: string) => {
      const path = ReactEditor.findPath(editor, element);
      setNodes(editor, { url }, { at: path });
    },
    [editor]
  );

  const handleOnReplace = useCallback(() => {
    const path = ReactEditor.findPath(editor, element);
    setNodes(editor, { url: undefined, width: undefined, caption: undefined }, { at: path });
  }, []);

  return (
    <div {...attributes}>
      <div contentEditable={false} className="my-12">
        {url !== undefined ? (
          <ImageRender
            isSelected={isSelected}
            caption={caption}
            imgWidth={width}
            nodeProps={nodeProps}
            onChangeImgWidth={handleChangeImageWidth}
            onChangeCaption={handleChangeCaption}
            onToggleCaption={onToggleCaption()}
            url={url}
            onDelete={handleOnDelete}
            onDuplicate={handleOnDuplicate}
            onReplace={handleOnReplace}
          />
        ) : (
          <UploadImageEditor onSubmit={onSetUrl} />
        )}
      </div>
      {children}
    </div>
  );
};
